<template>
  <div class="model" ref="model" :class="[
    { 'animate__animated animate__zoomOutLeft': isDeleting },
    { 'model--deleting': wasDeleted },
  ]" :style="{ maxHeight: `${modelHeight}px` }">
    <div class="model__name">
      <span @click.self="canEditCompany && (showModelNamePopup = true)" :style="{ cursor: canEditCompany ? 'pointer' : 'not-allowed' }">
        <transition name="fade" mode="in-out">
          <PopupEditer v-if="showModelNamePopup" @togglePopup="showModelNamePopup = $event" :selected="model.name"
            @submit="updateName($event)" />
        </transition>{{ model.name }}
      </span>
      <div class="model__delete">
        <div class="icon icon__delete" @click="canEditCompany && deleteModel()" :style="{ cursor: canEditCompany ? 'pointer' : 'not-allowed', opacity: canEditCompany ? 1 : 0.5 }"></div>
      </div>
    </div>
    <div class="model__categories">
      <ModelCategory v-for="category in model.model_categories" :key="category.id" :category="category"
        :company-id="model.company_id" :getModels="getModels" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import PopupEditer from "@/partials/PopupEditer";
import ModelCategory from "@/components/Contacts/ModelCategory";

defineOptions({
  name: "Model"
});

const props = defineProps({
  model: {
    required: true,
    type: Object,
  },
  getModels: {
    required: true,
    type: Function,
  },
});

const store = useStore();

// Computed
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const canEditCompany = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission('company_edit', props.model.company_id);
});
const isDeleting = ref(false);
const wasDeleted = ref(false);
const modelEl = ref(null);
const showModelNamePopup = ref(false);

// Computed
const modelHeight = computed(() => {
  return modelEl.value
    ? [...modelEl.value.children].reduce(
      (acc, child) => acc + child.clientHeight,
      0
    )
    : null;
});

// Lifecycle
onMounted(() => {
  // In <script setup>, template refs are accessed via the ref name
  // Assuming the ref in your template is named "model"
  // Note: Ensure your template has <div ref="model">
});

// Methods
const deleteModel = async () => {
  if (!canEditCompany.value) return;
  isDeleting.value = true;
  await store.dispatch("deleteModel", props.model.id);
  wasDeleted.value = true;

  setTimeout(async () => {
    await props.getModels();
  }, 300);
};

const updateName = async (name) => {
  if (!canEditCompany.value) return;
  await store.dispatch("updateModel", {
    name,
    modelId: props.model.id,
    serviceId: props.model.service_id,
    companyId: props.model.company_id,
  });
  await props.getModels();
};
</script>

<style scoped lang="scss">
.model {
  color: var(--text-10);
  text-decoration: underline;
  transition: all 0.3s ease;

  &--deleting {
    overflow: hidden;
    max-height: 0 !important;
    padding: 0 !important;

    .model__name {
      padding: 0px 20px 0px 50px;
    }
  }

  &__name {
    transition: all 0.3s ease;
    padding: 10px 20px 10px 50px;
  }

  &__name {
    cursor: pointer;

    span {
      position: relative;
    }
  }

  &__name,
  &__categories .category {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__categories {
    background: var(--bg-17);

    .category {
      padding: 10px 20px 10px 80px;
      max-height: 44px;

      &__name {
        font-family: var(--muli-text-italic);
      }
    }
  }
}

:deep(.category) {
  color: var(--text-10);
  text-decoration: underline;
  transition: all 0.3s ease;
  padding: 10px 20px 10px 80px;
  max-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

:deep(.category--deleting) {
  overflow: hidden;
  max-height: 0 !important;
  padding: 0 !important;
}

:deep(.category__name) {
  cursor: pointer;
  font-family: var(--muli-text-italic);
}
</style>
